import React from 'react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

/**
 * Recursive folder tree component
 */
const FolderTree = ({
  folders,
  selectedFolder,
  expandedFolders,
  onSelect,
  onToggleExpand,
  onCreateSubfolder,
  onRename,
  onDelete,
  onMove,
  level = 0,
}) => {
  return (
    <div className={level > 0 ? 'ml-4 border-l border-gray-200 pl-2' : ''}>
      {folders.map(folder => (
        <FolderItem
          key={folder.id}
          folder={folder}
          isSelected={selectedFolder?.id === folder.id}
          isExpanded={expandedFolders.has(folder.id)}
          onSelect={onSelect}
          onToggleExpand={onToggleExpand}
          onCreateSubfolder={onCreateSubfolder}
          onRename={onRename}
          onDelete={onDelete}
          onMove={onMove}
          level={level}
        >
          {folder.children && folder.children.length > 0 && expandedFolders.has(folder.id) && (
            <FolderTree
              folders={folder.children}
              selectedFolder={selectedFolder}
              expandedFolders={expandedFolders}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              onCreateSubfolder={onCreateSubfolder}
              onRename={onRename}
              onDelete={onDelete}
              onMove={onMove}
              level={level + 1}
            />
          )}
        </FolderItem>
      ))}
    </div>
  );
};

const FolderItem = ({
  folder,
  isSelected,
  isExpanded,
  onSelect,
  onToggleExpand,
  onCreateSubfolder,
  onRename,
  onDelete,
  onMove,
  children,
}) => {
  const hasChildren = folder.children && folder.children.length > 0;

  return (
    <div>
      <div
        className={`flex items-center gap-1 p-2 rounded-lg cursor-pointer transition-colors group ${
          isSelected ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'
        }`}
        data-testid={`folder-${folder.id}`}
      >
        {/* Expand/collapse button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(folder.id);
          }}
          className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600"
        >
          {hasChildren ? (isExpanded ? '▼' : '▶') : '•'}
        </button>

        {/* Folder name - click to select */}
        <div
          className="flex-1 flex items-center gap-2 min-w-0"
          onClick={() => onSelect(folder)}
        >
          <span>{isExpanded ? '📂' : '📁'}</span>
          <span className="truncate text-sm font-medium">{folder.name}</span>
        </div>

        {/* Actions dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
              onClick={(e) => e.stopPropagation()}
            >
              ⋮
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onCreateSubfolder(folder)}>
              ➕ Nieuwe submap
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onRename(folder)}>
              ✏️ Hernoemen
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onMove(folder)}>
              📦 Verplaatsen
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onDelete(folder)}
              className="text-red-600"
            >
              🗑️ Verwijderen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Children */}
      {children}
    </div>
  );
};

export default FolderTree;
